import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { manualArticles, manualCategories } from '../../../mocks/manuals';
import { mockPartners } from '../../../mocks/partners';
import RelatedLinks from '../../../components/feature/RelatedLinks';

export default function ManualArticlePage() {
  const { id } = useParams();
  const article = manualArticles.find(a => a.id === id);
  const [helpful, setHelpful] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">記事が見つかりません</h2>
          <Link to="/manual" className="text-teal-600 hover:underline cursor-pointer">マニュアルに戻る</Link>
        </div>
      </div>
    );
  }

  const category = manualCategories.find(c => c.id === article.categoryId);
  const authorPartner = article.authorId ? mockPartners.find(p => p.id === article.authorId) : null;
  const relatedArticles = manualArticles
    .filter(a => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const handleHelpful = () => {
    setHelpful(!helpful);
  };

  const handlePrint = () => {
    window.print();
  };

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    return lines.map((line, index) => {
      const trimmedLine = line.trim();
      
      if (trimmedLine.startsWith('# ')) {
        return <h1 key={index} className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 mt-8">{trimmedLine.substring(2)}</h1>;
      } else if (trimmedLine.startsWith('## ')) {
        return <h2 key={index} className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 mt-6">{trimmedLine.substring(3)}</h2>;
      } else if (trimmedLine.startsWith('### ')) {
        return <h3 key={index} className="text-lg sm:text-xl font-bold text-gray-800 mb-3 mt-4">{trimmedLine.substring(4)}</h3>;
      } else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
        return <p key={index} className="font-bold text-gray-800 mb-2">{trimmedLine.substring(2, trimmedLine.length - 2)}</p>;
      } else if (trimmedLine.startsWith('- [ ] ')) {
        return (
          <div key={index} className="flex items-start space-x-2 mb-2 ml-4">
            <input type="checkbox" className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer" />
            <span className="text-gray-700 text-sm sm:text-base">{trimmedLine.substring(6)}</span>
          </div>
        );
      } else if (trimmedLine.startsWith('- ')) {
        return (
          <div key={index} className="flex items-start space-x-2 mb-2 ml-4">
            <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
            <span className="text-gray-700 text-sm sm:text-base">{trimmedLine.substring(2)}</span>
          </div>
        );
      } else if (trimmedLine.startsWith('❌ ')) {
        return (
          <div key={index} className="flex items-start space-x-2 mb-2 ml-4 text-rose-600">
            <i className="ri-close-circle-fill mt-1"></i>
            <span className="text-sm sm:text-base">{trimmedLine.substring(2)}</span>
          </div>
        );
      } else if (trimmedLine.startsWith('→ ')) {
        return <p key={index} className="text-rose-600 font-medium mb-2 ml-4 text-sm sm:text-base">{trimmedLine.substring(2)}</p>;
      } else if (trimmedLine.startsWith('---')) {
        return <hr key={index} className="my-6 border-gray-200" />;
      } else if (trimmedLine === '') {
        return <div key={index} className="h-2"></div>;
      } else {
        return <p key={index} className="text-gray-700 mb-3 leading-relaxed text-sm sm:text-base">{trimmedLine}</p>;
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      {/* ナビゲーション */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-amber-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-ship-line text-white text-xl"></i>
              </div>
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">スマイル・ポート</span>
            </Link>
            
            {/* デスクトップメニュー */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">ホーム</Link>
              <Link to="/board" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">みんなの休憩室</Link>
              <Link to="/manual" className="text-teal-600 font-medium whitespace-nowrap cursor-pointer">お守りマニュアル</Link>
              <button className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
                ログイン
              </button>
            </div>

            {/* モバイルメニューボタン */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:text-teal-600 cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* モバイルメニュー */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-amber-100">
              <div className="flex flex-col space-y-3">
                <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer py-2">ホーム</Link>
                <Link to="/board" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer py-2">みんなの休憩室</Link>
                <Link to="/manual" className="text-teal-600 font-medium cursor-pointer py-2">お守りマニュアル</Link>
                <button className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
                  ログイン
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* パンくずリスト */}
        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
          <Link to="/" className="hover:text-teal-600 cursor-pointer">ホーム</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/manual" className="hover:text-teal-600 cursor-pointer">お守りマニュアル</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-800 break-words">{article.title}</span>
        </div>

        {/* 記事ヘッダー */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-${category?.color}-100`}>
              <i className={`${category?.icon} text-${category?.color}-600 text-xl sm:text-2xl`}></i>
            </div>
            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-${category?.color}-100 text-${category?.color}-700`}>
              {category?.name}
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">{article.title}</h1>
          <p className="text-gray-600 text-base sm:text-lg mb-6">{article.summary}</p>

          {/* 執筆者パートナー情報 */}
          {authorPartner && (
            <Link
              to={`/partner/profile/${authorPartner.id}`}
              className="flex items-center gap-4 p-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl mb-6 hover:shadow-md transition-all cursor-pointer group"
            >
              <img
                src={authorPartner.avatar_url}
                alt={authorPartner.nickname}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-white shadow-sm"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs text-gray-500">執筆者</span>
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-medium">
                    {authorPartner.partner_role}
                  </span>
                </div>
                <p className="font-bold text-gray-800 group-hover:text-teal-600 transition-colors text-sm sm:text-base">
                  {authorPartner.nickname}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 line-clamp-1">{authorPartner.area}</p>
              </div>
              <div className="flex items-center text-teal-600 group-hover:translate-x-1 transition-transform">
                <span className="text-xs sm:text-sm mr-1 hidden sm:inline">プロフィールを見る</span>
                <i className="ri-arrow-right-s-line text-xl"></i>
              </div>
            </Link>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <i className="ri-calendar-line"></i>
                <span>更新日: {new Date(article.updatedAt).toLocaleDateString('ja-JP')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-eye-line"></i>
                <span>{article.views} 閲覧</span>
              </div>
            </div>

            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2 text-sm"
            >
              <i className="ri-printer-line"></i>
              <span>印刷</span>
            </button>
          </div>
        </div>

        {/* 記事本文 */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 mb-6">
          <div className="prose prose-sm sm:prose-lg max-w-none">
            {renderContent(article.content)}
          </div>
        </div>

        {/* 役に立ったボタン */}
        <div className="bg-gradient-to-br from-teal-100 to-emerald-100 rounded-2xl shadow-sm p-6 sm:p-8 mb-6 text-center">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">この記事は役に立ちましたか？</h3>
          <button
            onClick={handleHelpful}
            className={`px-6 sm:px-8 py-3 sm:py-4 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
              helpful
                ? 'bg-teal-500 text-white shadow-lg'
                : 'bg-white text-teal-700 hover:bg-teal-50'
            }`}
          >
            <div className="flex items-center space-x-2">
              <i className={helpful ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
              <span className="text-sm sm:text-base">役に立った</span>
              <span className="font-bold text-sm sm:text-base">({article.helpful + (helpful ? 1 : 0)})</span>
            </div>
          </button>
          <p className="text-gray-600 text-xs sm:text-sm mt-4">
            ご意見やリクエストは「みんなの休憩室」でお聞かせください
          </p>
        </div>

        {/* 関連記事 */}
        {relatedArticles.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">関連記事</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/manual/${related.id}`}
                  className="p-4 border-2 border-gray-200 rounded-xl hover:border-teal-500 hover:shadow-md transition-all cursor-pointer"
                >
                  <h4 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">{related.title}</h4>
                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{related.summary}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* 戻るボタン */}
        <div className="text-center">
          <Link
            to="/manual"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-white text-gray-700 rounded-xl hover:shadow-md transition-all cursor-pointer text-sm sm:text-base"
          >
            <i className="ri-arrow-left-line"></i>
            <span>マニュアル一覧に戻る</span>
          </Link>
        </div>
      </div>

      {/* 関連リンク */}
      <RelatedLinks />

      {/* フッター */}
      <footer className="bg-gradient-to-r from-teal-700 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-ship-line text-white text-xl"></i>
                </div>
                <span className="text-lg sm:text-xl font-bold">みやぎスマイル・ポート</span>
              </div>
              <p className="text-teal-100 text-sm">医療的ケア児と家族の港</p>
            </div>
            <div>
              <h4 className="font-bold mb-3">リンク</h4>
              <ul className="space-y-2 text-sm text-teal-100">
                <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">ホーム</Link></li>
                <li><Link to="/board" className="hover:text-white transition-colors cursor-pointer">みんなの休憩室</Link></li>
                <li><Link to="/manual" className="hover:text-white transition-colors cursor-pointer">お守りマニュアル</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">お問い合わせ</h4>
              <p className="text-sm text-teal-100">support@smile-port.jp</p>
            </div>
          </div>
          <div className="border-t border-teal-600 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-teal-100">
            <p>&copy; 2025 みやぎスマイル・ポート. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
